import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import AvatarService from "@services/AvatarService";
import {v4 as uuidv4} from "uuid";

/** В Redux всегда секунды: product/scenario — как в JSON (Duration), certification — минуты из API × 60. */
/** certificationMinutes можно не использовать, баг на бэкенде пофикшен */
const sessionDurationSecFromCreatePayload = (raw, certificationMinutes) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
};

const hasFeedbackPayload = (data) => {
  const hasJson =
    Array.isArray(data?.feedback_json) && data.feedback_json.length > 0;
  const hasText =
    typeof data?.feedback === "string"
      ? data.feedback.trim() !== ""
      : Boolean(data?.feedback);

  return hasJson || hasText;
};

export const getProxyList = createAsyncThunk("get/proxy_list", async () => {
  const response = await AvatarService.getProxyList();
  return response.data;
});

export const createScenarioSession = createAsyncThunk(
  "create/scenario_session",
  async ({level, scenario_id}) => {
    const response = await AvatarService.createScenarioSession({
      level,
      scenario_id,
    });

    return response.data;
  },
);

export const createProductSession = createAsyncThunk(
  "create/product_session",
  async ({level, product_id}) => {
    const response = await AvatarService.createProductSession({
      level,
      product_id,
    });

    return response.data;
  },
);

export const createCertificateSession = createAsyncThunk(
  "create/certificate_session",
  async ({level, scenario_id, session_format}) => {
    const response = await AvatarService.createCertificateSession({
      level,
      scenario_id,
      session_format,
    });

    return response.data;
  },
);

export const sendMessage = createAsyncThunk(
  "send/message",
  async ({id, msg}) => {
    const response = await AvatarService.sendMessage({id, msg});

    return response.data;
  },
);

export const sendCertificateMessage = createAsyncThunk(
  "send/certificate_message",
  async ({id, msg}) => {
    const response = await AvatarService.sendCertificateMessage({id, msg});

    return response.data;
  },
);

export const getQuestion = createAsyncThunk("get/question", async ({id}) => {
  const response = await AvatarService.getQuestion({id});

  return response.data;
});

export const sendAnswer = createAsyncThunk(
  "send/answer",
  async ({id, answer_id, answer}) => {
    const response = await AvatarService.sendAnswer({id, answer_id, answer});

    return response.data;
  },
);

export const getScenarioFeedback = createAsyncThunk(
  "session/scenario-feedback",
  async (id) => {
    let attempts = 0;
    const maxAttempts = 9;

    while (attempts < maxAttempts) {
      const response = await AvatarService.getScenarioFeedback(id);
      const {delay} = response.data;

      if (hasFeedbackPayload(response.data)) {
        return response.data;
      } else if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        attempts++;
      } else {
        return response.data;
      }
    }

    const finalResponse = await AvatarService.getScenarioFeedback(id);

    if (hasFeedbackPayload(finalResponse?.data)) {
      return finalResponse.data;
    } else {
      return {feedback: "generated"};
    }
  },
);

export const getCertificateFeedback = createAsyncThunk(
  "session/certificate-feedback",
  async (id) => {
    let attempts = 0;
    const maxAttempts = 9;

    while (attempts < maxAttempts) {
      const response = await AvatarService.getCertificateFeedback(id);
      const {delay} = response.data;

      if (hasFeedbackPayload(response.data)) {
        return response.data;
      } else if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        attempts++;
      } else {
        return response.data;
      }
    }

    const finalResponse = await AvatarService.getCertificateFeedback(id);

    if (hasFeedbackPayload(finalResponse?.data)) {
      return finalResponse.data;
    } else {
      return {feedback: "generated"};
    }
  },
);

export const getProductFeedback = createAsyncThunk(
  "session/product-feedback",
  async (id) => {
    let attempts = 0;
    const maxAttempts = 9;

    while (attempts < maxAttempts) {
      const response = await AvatarService.getProductFeedback(id);
      const {delay} = response.data;

      if (hasFeedbackPayload(response.data)) {
        return response.data;
      } else if (delay) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        attempts++;
      } else {
        return response.data;
      }
    }

    const finalResponse = await AvatarService.getProductFeedback(id);
    return finalResponse.data;
  },
);

export const getRecordToken = createAsyncThunk("get/record-token", async () => {
  const response = await AvatarService.getRecordToken();

  return response.data;
});

export const getAvatarToken = createAsyncThunk(
  "get/avatar-token",
  async (payload = {}) => {
    const response = await AvatarService.getAvatarToken(payload);

    return response.data;
  },
);

export const getAnamAiToken = createAsyncThunk(
  "get/anam-ai-token",
  async (payload = {}) => {
    const response = await AvatarService.getAnamAiToken(payload);

    return response.data;
  },
);

export const getAkoolToken = createAsyncThunk("get/akool-token", async () => {
  const response = await AvatarService.getAkoolToken();

  return response.data;
});

const initialState = {
  avatarId: "",
  voiceId: "",
  sessionDurationSec: null,
  scenario_session_id: null,
  product_session_id: null,
  certificate_session_id: null,
  sessionState: "",
  userMessage: "",
  botMessage: "",
  messages: [],
  score: null,
  conclusion: null,
  questions: null,
  answer_id: null,
  isRecording: false,
  recordToken: null,
  recordHost: null,
  sttErrorKey: null,
  volume: 0.2,
  start_time: null,
  schedule_intent: null,
  error: "",
  serverStatus: "",
  feedbackStatus: {state: "", session: ""},
  feedback: "",
  feedback_json: null,
  feedback_score: null,
  max_feedback_score: null,
  certified: false,
  proxyList: {},
  successful: false,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setSttErrorKey(state, action) {
      state.sttErrorKey = action.payload || null;
    },
    setScheduleIntent(state, action) {
      state.schedule_intent = action.payload;
    },
    setAvatarId(state, action) {
      state.avatarId = action.payload;
    },
    setVoiceId(state, action) {
      state.voiceId = action.payload;
    },
    setScore(state, action) {
      state.score = action.payload;
    },
    setScenarioSessionId(state, action) {
      state.scenario_session_id = action.payload;
    },
    setProductSessionId(state, action) {
      state.product_session_id = action.payload;
    },
    setCertificateSessionId(state, action) {
      state.certificate_session_id = action.payload;
    },
    setIsRecording(state, action) {
      state.isRecording = action.payload;
    },
    setAnswerId(state, action) {
      state.answer_id = action.payload;
    },
    setQuestions(state, action) {
      state.questions = action.payload;
    },
    setUserMessage(state, action) {
      state.userMessage = action.payload;
    },
    setBotMessage(state, action) {
      state.botMessage = action.payload;
    },
    setMessages(state, action) {
      state.messages.push(action.payload);
    },
    setVolume(state, action) {
      state.volume = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setFeedback(state, action) {
      state.feedback = action.payload;
    },
    setFeedbackScore(state, action) {
      state.feedback_score = action.payload;
    },
    setFeedbackJson(state, action) {
      state.feedback_json = action.payload;
    },
    setFeedbackStatus(state, action) {
      state.feedbackStatus = {
        state: action.payload.state,
        session: action.payload.session,
      };
    },
    setServerStatus(state, action) {
      state.serverStatus = action.payload;
    },
    setStartTime(state, action) {
      state.start_time = action.payload;
    },
    setProxyList(state, action) {
      state.proxyList = action.payload;
    },
    clearMessages(state) {
      state.messages = [];
    },
    clearChatState(state) {
      state.schedule_intent = null;
      state.userMessage = "";
      state.botMessage = "";
      state.answer_id = null;
      state.score = null;
      state.conclusion = null;
      state.messages = [];
      state.sessionState = "";
      state.sessionDurationSec = null;
      state.questions = null;
      state.recordToken = null;
      state.isRecording = false;
      state.recordHost = null;
      state.sttErrorKey = null;
      state.start_time = null;
      state.feedback = "";
      state.error = "";
      state.feedback_score = null;
      state.feedback_json = null;
      state.max_feedback_score = null;
      state.certified = false;
      state.proxyList = {};
      state.successful = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // getProxyList
      .addCase(getProxyList.pending, (state) => {
        state.proxyList = {};
        state.serverStatus = "loading";
      })
      .addCase(getProxyList.fulfilled, (state, action) => {
        state.proxyList = action?.payload;
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(getProxyList.rejected, (state) => {
        state.error = "Don`t get proxy list!";
        state.serverStatus = "error";
      })

      // createScenarioSession
      .addCase(createScenarioSession.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionDurationSec = null;
      })
      .addCase(createScenarioSession.fulfilled, (state, action) => {
        state.scenario_session_id = action?.payload?.id;
        state.sessionDurationSec = sessionDurationSecFromCreatePayload(
          action?.payload?.duration,
          false,
        );
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(createScenarioSession.rejected, (state) => {
        state.error = "Don`t create scenario session!";
        state.serverStatus = "error";
      })

      // createProductSession
      .addCase(createProductSession.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionDurationSec = null;
      })
      .addCase(createProductSession.fulfilled, (state, action) => {
        state.product_session_id = action?.payload?.id;
        state.questions = action?.payload?.questions_count;
        state.sessionDurationSec = sessionDurationSecFromCreatePayload(
          action?.payload?.duration,
          false,
        );
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(createProductSession.rejected, (state) => {
        state.error = "Don`t create product session!";
        state.serverStatus = "error";
      })

      // createCertificateSession
      .addCase(createCertificateSession.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionDurationSec = null;
      })
      .addCase(createCertificateSession.fulfilled, (state, action) => {
        state.certificate_session_id = action?.payload?.id;
        state.sessionDurationSec = sessionDurationSecFromCreatePayload(
          action?.payload?.duration,
          true,
        );
        state.error = "";
        state.serverStatus = "success";
      })
      .addCase(createCertificateSession.rejected, (state) => {
        state.error = "Don`t create certificate session!";
        state.serverStatus = "error";
      })

      // getScenarioFeedback
      .addCase(getScenarioFeedback.pending, (state) => {
        state.serverStatus = "loading";
        state.feedbackStatus = {state: "pending", session: "scenario"};
      })
      .addCase(getScenarioFeedback.fulfilled, (state, action) => {
        state.feedback = action.payload.feedback || "";
        state.feedback_score = action?.payload?.feedback_score;
        state.feedback_json = action?.payload?.feedback_json;
        state.max_feedback_score = action?.payload?.max_possible_score;
        state.error = "";
        state.serverStatus = hasFeedbackPayload(action.payload)
          ? "success"
          : "loading";
        state.feedbackStatus = {state: "completed", session: "scenario"};
        state.successful = action?.payload?.successful;
      })
      .addCase(getScenarioFeedback.rejected, (state) => {
        state.error = "Don`t get scenario feedback!";
        state.serverStatus = "error";
        state.feedbackStatus = {state: "error", session: "scenario"};
      })

      // getProductFeedback
      .addCase(getProductFeedback.pending, (state) => {
        state.serverStatus = "loading";
        state.feedbackStatus = {state: "pending", session: "product"};
      })
      .addCase(getProductFeedback.fulfilled, (state, action) => {
        state.feedback = action.payload.feedback || "";
        state.feedback_json = action?.payload?.feedback_json;
        state.error = "";
        state.serverStatus = hasFeedbackPayload(action.payload)
          ? "success"
          : "loading";
        state.feedbackStatus = {state: "completed", session: "product"};
      })
      .addCase(getProductFeedback.rejected, (state) => {
        state.error = "Don`t get product feedback!";
        state.serverStatus = "error";
        state.feedbackStatus = {state: "error", session: "product"};
      })

      // getCertificateFeedback
      .addCase(getCertificateFeedback.pending, (state) => {
        state.serverStatus = "loading";
        state.feedbackStatus = {state: "pending", session: "certification"};
      })
      .addCase(getCertificateFeedback.fulfilled, (state, action) => {
        state.feedback = action.payload.feedback || "";
        state.feedback_score = action?.payload?.feedback_score;
        state.feedback_json = action?.payload?.feedback_json;
        state.max_feedback_score = action?.payload?.max_possible_score;
        state.certified = action?.payload?.certified;
        state.error = "";
        state.serverStatus = hasFeedbackPayload(action.payload)
          ? "success"
          : "loading";
        state.feedbackStatus = {state: "completed", session: "certification"};
      })
      .addCase(getCertificateFeedback.rejected, (state) => {
        state.error = "Don`t get certificate feedback!";
        state.serverStatus = "error";
        state.feedbackStatus = {state: "error", session: "certification"};
      })

      // sendMessage
      .addCase(sendMessage.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionState = "";
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.botMessage = action.payload.ai;
        state.score = action.payload.score;
        state.sessionState = action.payload.state;
        state.error = "";
        state.serverStatus = "success";
        state.schedule_intent = action.payload.schedule_intent;

        if (action.payload) {
          const bot = {
            messageId: uuidv4(),
            text: state.botMessage,
            sender: "bot",
          };

          state.messages.push(bot);
        }
      })
      .addCase(sendMessage.rejected, (state) => {
        state.error = "Don`t send message!";
        state.sessionState = "";
        state.serverStatus = "error";
      })

      // sendCertificateMessage
      .addCase(sendCertificateMessage.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionState = "";
      })
      .addCase(sendCertificateMessage.fulfilled, (state, action) => {
        state.botMessage = action.payload.ai;
        state.score = action.payload.score;
        state.sessionState = action.payload.state;
        state.error = "";
        state.serverStatus = "success";
        state.schedule_intent = action.payload.schedule_intent;

        if (action.payload) {
          const bot = {
            messageId: uuidv4(),
            text: state.botMessage,
            sender: "bot",
          };

          state.messages.push(bot);
        }
      })
      .addCase(sendCertificateMessage.rejected, (state) => {
        state.error = "Don`t send message!";
        state.sessionState = "";
        state.serverStatus = "error";
      })

      // getQuestion
      .addCase(getQuestion.pending, (state) => {
        state.serverStatus = "loading";
      })
      .addCase(getQuestion.fulfilled, (state, action) => {
        state.botMessage = action.payload.text;
        state.answer_id = action.payload.answer_id;
        state.error = "";
        state.serverStatus = "success";

        if (action.payload) {
          const bot = {
            messageId: uuidv4(),
            text: state.botMessage,
            sender: "bot",
          };

          state.messages.push(bot);
          state.questions = state.questions - 1;
        }
      })
      .addCase(getQuestion.rejected, (state) => {
        state.error = "Don`t get question!";
        state.serverStatus = "error";
      })

      // sendAnswer
      .addCase(sendAnswer.pending, (state) => {
        state.serverStatus = "loading";
        state.sessionState = "";
      })
      .addCase(sendAnswer.fulfilled, (state, action) => {
        const {score, conclusion, state: sessionState} = action.payload;

        state.score = score;
        state.conclusion = conclusion ?? null;
        state.sessionState = sessionState ?? "";
        state.error = "";
        state.serverStatus = "success";

        if (conclusion) {
          state.messages.push({
            messageId: uuidv4(),
            text: conclusion,
            sender: "bot",
            kind: "conclusion",
            score,
          });
        }

        if (sessionState === "ended") {
          state.messages.push({
            messageId: uuidv4(),
            sender: "bot",
            kind: "product_session_complete",
          });
        }
      })
      .addCase(sendAnswer.rejected, (state) => {
        state.error = "Don`t send answer!";
        state.sessionState = "";
        state.serverStatus = "error";
      })

      // getRecordToken
      .addCase(getRecordToken.pending, (state) => {
        state.recordToken = null;
        state.recordHost = null;
        state.sttErrorKey = null;
      })
      .addCase(getRecordToken.fulfilled, (state, action) => {
        state.recordToken =
          action?.payload?.access_token ||
          action?.payload?.accessToken ||
          action?.payload?.token ||
          null;
        state.recordHost =
          action?.payload?.host ||
          action?.payload?.url ||
          action?.payload?.base_url ||
          null;

        // /stt_jwt может вернуть 200 OK, но с { error: "..." } (например 503 от STT сервиса).
        if (action?.payload?.error || !state.recordToken || !state.recordHost) {
          state.sttErrorKey = "server_error.stt_unavailable";
        } else {
          state.sttErrorKey = null;
        }
        state.error = "";
      })
      .addCase(getRecordToken.rejected, (state) => {
        state.error = "Don`t get record token!";
        state.recordToken = null;
        state.recordHost = null;
        state.sttErrorKey = "server_error.stt_unavailable";
      });
  },
});

export const {
  setScore,
  setCertificateSessionId,
  setScenarioSessionId,
  setProductSessionId,
  setQuestions,
  setBotMessage,
  setUserMessage,
  setMessages,
  setVolume,
  setError,
  setServerStatus,
  clearChatState,
  setIsRecording,
  setAnswerId,
  setAvatarId,
  setVoiceId,
  clearMessages,
  setFeedback,
  setStartTime,
  setScheduleIntent,
  setFeedbackStatus,
  setFeedbackJson,
  setSttErrorKey,
} = chatSlice.actions;
export default chatSlice.reducer;
